// src/lib/fils-attente-ordre.js — QUELS FILS ATTENDENT, ET DANS QUEL ORDRE.
//
// ⚠️ DUPLICATION CONTRÔLÉE — Pattern ADR-021. Miroir ESM : src/lib/. Toute
// modification s'applique AUX DEUX, et un test compare les octets.
//
// ══ POURQUOI CE MODULE EXISTE ═══════════════════════════════════════════════
//
// Dimitri, le 2026-09-12 :
//
//   « L'opérateur ne voit pas les mêmes messages que mon écran. J'ai cinq
//     personnes, dont Chloé Raynal — la plus récente. Lui me dit qu'il en voit
//     huit, que le plus récent date de onze jours, et que Chloé n'existe nulle
//     part. Je lui ai demandé de préparer une réponse pour elle, il m'a
//     répondu qu'il ne la connaissait pas et m'a proposé de coller son message
//     à la main. C'est exactement ce qu'un bras droit ne doit pas faire. »
//
// ⚠️ LES DEUX LISAIENT LA MÊME TABLE. Le défaut n'était pas dans la source,
// il était dans l'ORDRE :
//
//   · l'écran trie du plus RÉCENT au plus ancien, et montre les six premiers ;
//   · l'opérateur triait du plus ANCIEN au plus récent — « ce sont eux qui
//     coûtent une réservation » — puis coupait au même plafond.
//
// Deux tris opposés, un même plafond : ils gardaient des fils DISJOINTS. Chloé
// (deux jours) tombait en queue de la liste de l'opérateur et disparaissait,
// pendant qu'elle occupait la première ligne de l'écran de Dimitri.
//
// L'intention de départ était juste — un fil qui traîne coûte cher. Mais elle
// avait déjà sa maison : la veille `fil_sans_reponse` signale les fils
// anciens, dans « À surveiller ». En la rejouant ici, on n'ajoutait pas un
// éclairage : on retirait de sa vue les messages du jour.
//
// ⚠️ CE MODULE EST PUR : il ne lit rien, il ne demande rien. Il range.

/**
 * Les fils dont le DERNIER message est entrant — donc qui attendent.
 *
 * @param {Array} messages — des lignes `inbox_messages`, dans N'IMPORTE quel
 *   ordre : la fonction ne suppose rien de l'appelant. Supposer un tri d'entrée
 *   était déjà la moitié du défaut qu'on corrige.
 * @returns {Array} le dernier message de chaque fil en attente, du plus RÉCENT
 *   au plus ancien — l'ordre de l'écran de Dimitri, qui fait foi.
 */
function filsQuiAttendent(messages) {
  const dernier = new Map();
  for (const m of messages || []) {
    if (!m) continue;
    // ⚠️ UNE RÉPONSE RÉSERVÉE N'EST PAS UNE RÉPONSE (29/09, N1 revue). Un
    // sortant marqué en_cours / echec_net / incertain n'est pas (sûrement)
    // parti : il ne referme pas le fil. Sans marque = ancien chemin, inchangé.
    if (_sortantNonParti(m)) continue;
    const fil = m.thread_id || m.id;
    if (!fil) continue;
    const vu = dernier.get(fil);
    // ⚠️ ON COMPARE LES DATES, on ne se fie pas à l'ordre d'arrivée. C'est ce
    // qui rend les deux appelants identiques quoi qu'ils envoient.
    if (!vu || _quand(m) > _quand(vu)) dernier.set(fil, m);
  }
  const out = [];
  for (const m of dernier.values()) if (m.direction === "inbound") out.push(m);
  // Le plus récent d'abord. Un plafond appliqué à cette liste garde donc ce qui
  // attend MAINTENANT — et non les six plus vieux, qui ont déjà leur ligne
  // dans « À surveiller ».
  out.sort((a, b) => _quand(b) - _quand(a));
  return out;
}

// Miroir de lib/inbox-envoi-statut.js `sortantNonParti` (src/ ne peut pas
// importer lib/) : un test compare les deux sur la même table de vérité.
const _ENVOI_NON_PARTI = ["en_cours", "echec_net", "incertain"];
function _sortantNonParti(m) {
  if (!m || m.direction !== "outbound") return false;
  const e =
    typeof m.envoi_statut === "string"
      ? m.envoi_statut
      : m.metadata && typeof m.metadata.envoi_statut === "string"
        ? m.metadata.envoi_statut
        : null;
  return _ENVOI_NON_PARTI.includes(e);
}

function _quand(m) {
  const t = new Date((m && m.created_at) || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

export { filsQuiAttendent };
export const _internal = { _quand, _sortantNonParti };
